import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, App as AntdApp, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessBlockCreate,
  organizationsAccessDestroy
} from 'src/api/main'

import { invalidateWorkspaceUserQueries } from '../hooks'
import type { WorkspaceUser } from '../types'

const { Text } = Typography

export type BulkAction = 'suspend' | 'reactivate' | 'delete'

type Props = {
  workspaceId: number
  open: boolean
  action: BulkAction
  users: WorkspaceUser[]
  onClose: () => void
  /** Called with the IDs that succeeded — handy for clearing selection. */
  onSuccessIds?: (ids: number[]) => void
}

/**
 * Fans out a per-user API call across `users`. There is no server-side bulk
 * endpoint, so we issue one request per record in parallel and aggregate the
 * outcome into a single success/failure toast.
 */
export const UserBulkActionModal = ({
  workspaceId,
  open,
  action,
  users,
  onClose,
  onSuccessIds
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      const tasks = users.map((u) =>
        action === 'delete'
          ? organizationsAccessDestroy({ path: { id: u.id } })
          : organizationsAccessBlockCreate({
              path: { id: u.id },
              body: { blocked_value: action === 'suspend' }
            })
      )
      const results = await Promise.allSettled(tasks)
      const succeededIds: number[] = []
      const failedIds: number[] = []
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') succeededIds.push(users[i].id)
        else failedIds.push(users[i].id)
      })
      return { succeededIds, failedIds }
    },
    onSuccess: async ({ succeededIds, failedIds }) => {
      const succeededIdSet = new Set(succeededIds)
      await Promise.all(
        users
          .filter((user) => succeededIdSet.has(user.id))
          .map((user) =>
            invalidateWorkspaceUserQueries(qc, {
              workspaceId,
              organizationAccessId: user.id,
              userId: user.user.id
            })
          )
      )
      if (succeededIds.length > 0) {
        message.success(
          t(`users.modals.bulk.${action}.success`, {
            count: succeededIds.length
          })
        )
        onSuccessIds?.(succeededIds)
      }
      if (failedIds.length > 0) {
        message.error(
          t(`users.modals.bulk.${action}.partial`, {
            count: failedIds.length
          })
        )
      }
      if (failedIds.length === 0) onClose()
    }
  })

  const count = users.length
  const danger = action !== 'reactivate'

  return (
    <Modal
      open={open}
      centered
      title={t(`users.modals.bulk.${action}.title`, { count })}
      okText={t(`users.modals.bulk.${action}.submit`)}
      cancelText={t('users.modals.cancel')}
      okType={danger ? 'danger' : 'primary'}
      okButtonProps={{ loading: isPending, disabled: count === 0 }}
      cancelButtonProps={{ disabled: isPending }}
      onOk={() => void mutateAsync()}
      onCancel={() => {
        if (!isPending) onClose()
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0, color: 'var(--ant-color-text-secondary)' }}>
        {t(`users.modals.bulk.${action}.description`, { count })}
      </p>
      {danger ? (
        <Alert
          type="warning"
          showIcon
          title={t(`users.modals.bulk.${action}.warning`)}
        />
      ) : null}
      <Text
        type="secondary"
        style={{ fontSize: 12, display: 'block', marginTop: 10 }}
      >
        {t('users.modals.bulk.note')}
      </Text>
    </Modal>
  )
}
